import { useState, type FC } from 'react';
import {
  Accordion, AccordionItem, ClickableTile, InlineNotification, Layer, ListItem, StructuredListBody, StructuredListCell, StructuredListHead, StructuredListRow, StructuredListWrapper, Tab, TabList, TabPanel, TabPanels, Tabs, Table, TableBody, TableCell, TableContainer, TableHead, TableHeader, TableRow, Tag, Tile, UnorderedList,
} from '@carbon/react';
import { SimpleBarChart } from '@carbon/charts-react';
import { UserSpeaker } from '@carbon/icons-react';

import type { DimensaoDoRelatorio, RelatorioReal as DadosDoRelatorio } from '../../../../logica/desenvolvimento/relatorioReal';
import { numeroCurto } from '../../../../logica/desenvolvimento/relatorioReal';
import { ROTULOS, rotuloDoAviso } from '../../../padroes/rotulos';
import { Secao } from '../../../padroes/Secao';
import { useDoMd } from '../../../padroes/useDoMd';
import { useTemaDaPagina } from '../../../padroes/useTemaDaPagina';
import styles from './Diagnostico.module.scss';
import { AvisoComAcao } from '../../../padroes/AvisoComAcao';
import { DicaDaMaestra } from '../../../padroes/DicaDaMaestra';

// O RELATÓRIO DO DIAGNÓSTICO REAL, no Carbon: o perfil (nome, as quatro letras e o retrato), as
// dimensões em abas contidas (o selo na segunda linha da aba; no painel, a nota, a leitura, os
// números, o aviso e o que os dados revelam; o E com as finanças), a audiência nas plataformas e a
// posição entre os 16 perfis. Todo o conteúdo vem pronto da lógica (`relatorioReal`), que só lê o
// que o motor gravou.
//
// É o MESMO relatório na página do Desenvolvimento e na entrega depois do questionário: quem o
// usa põe as ações em volta.

/** Partes inteiras que somam 100 (os maiores restos recebem os pontos que o arredondamento tira). */
const partesInteiras = (valores: number[]) => {
  const todo = valores.reduce((s, v) => s + v, 0) || 1;
  const brutas = valores.map((v) => (v / todo) * 100);
  const inteiras = brutas.map(Math.floor);
  const falta = valores.length ? 100 - inteiras.reduce((s, v) => s + v, 0) : 0;
  brutas.map((b, i) => ({ i, resto: b - inteiras[i] })).sort((a, b) => b.resto - a.resto).slice(0, falta).forEach(({ i }) => { inteiras[i] += 1; });
  return inteiras.map((n) => `${n}%`);
};

/** As quatro letras R·E·A·L: a acesa com peso e traço, a apagada no tom de apoio (o mesmo do Início). */
export const LetrasReal: FC<{ dimensoes: { letra: string; acesa: boolean }[]; rotulo?: string; grande?: boolean }> = ({ dimensoes, rotulo, grande }) => (
  <span className={`${styles.letras} ${grande ? styles.letrasGrandes : ''}`} role='img'
    aria-label={rotulo ?? `Dimensões acesas: ${dimensoes.filter((d) => d.acesa).map((d) => d.letra).join(', ') || 'nenhuma'}`}>
    {dimensoes.map((d) => <span key={d.letra} aria-hidden className={d.acesa ? styles.acesa : styles.apagada}>{d.letra}</span>)}
  </span>
);

/** A linha que veio do questionário (autorrelato): o rótulo e o ícone de quem declarou. */
const Declarado: FC<{ rotulo: string }> = ({ rotulo }) => (
  <span className={styles.declarado}>
    {rotulo}
    <UserSpeaker size={16} aria-label='informado por quem preencheu' className={styles.iconeDoDeclarado} />
  </span>
);

/** Uma lista de duas ou três colunas, com os números alinhados à direita. */
const Lista: FC<{ rotulo: string; cabecas?: string[]; linhas: { chave: string; celulas: React.ReactNode[] }[] }> = ({ rotulo, cabecas, linhas }) => (
  <StructuredListWrapper isCondensed aria-label={rotulo} className={styles.lista}>
    {cabecas && (
      <StructuredListHead>
        <StructuredListRow head>
          {cabecas.map((c, i) => <StructuredListCell head key={c} className={i > 0 ? styles.valor : undefined}>{c}</StructuredListCell>)}
        </StructuredListRow>
      </StructuredListHead>
    )}
    <StructuredListBody>
      {linhas.map((l) => (
        <StructuredListRow key={l.chave}>
          {/* eslint-disable-next-line react/no-array-index-key */}
          {l.celulas.map((c, i) => <StructuredListCell key={`${l.chave}-${i}`} className={i > 0 ? styles.valor : undefined}>{c}</StructuredListCell>)}
        </StructuredListRow>
      ))}
    </StructuredListBody>
  </StructuredListWrapper>
);

/** Os subtítulos de uma dimensão descem um nível quando ela mesma tem título (a entrega em sequência). */
type Nivel = 'h3' | 'h4' | 'h5';
const abaixo = (n: Nivel): Nivel => (n === 'h3' ? 'h4' : 'h5');

const Dimensao: FC<{ d: DimensaoDoRelatorio; nivel?: Nivel }> = ({ d, nivel = 'h3' }) => {
  const H = nivel;
  const Hsub = abaixo(nivel);
  const f = d.financas;
  // Um aviso só por dimensão, no mais grave: o saldo negativo vence os informativos.
  const avisos = [...(f?.avisoDoSaldo ? [f.avisoDoSaldo] : []), ...d.avisos];
  const numeros = [
    // O autorrelato leva o ícone de quem declarou (era a adaga †), na tela e no PDF.
    ...d.linhas.map((l) => ({ chave: l.rotulo, celulas: [l.declarado ? <Declarado rotulo={l.rotulo} /> : l.rotulo, l.valor] })),
    // CNPJ e empresário sempre os dois, positivo ou negativo (§3), como linhas: é dado, e não selo.
    ...(d.estrutura ?? []).map((e) => ({ chave: e.rotulo, celulas: [e.rotulo === 'CNPJ' ? 'Tem CNPJ' : 'Tem empresário', e.tem ? 'Sim' : 'Não'] })),
  ];
  const composicao = [...(f?.composicao ?? [])].sort((a, b) => b.valor - a.valor);
  const partes = partesInteiras(composicao.map((c) => c.valor));
  return (
    <div className={styles.dimensao}>
      <div className={styles.cabecaDaDimensao}>
        {d.nota != null && <p className={styles.nota}>{d.nota}<span className={styles.notaMaxima}>/100</span></p>}
        {/* O selo já está na segunda linha da aba: aqui fica só o status. */}
        {d.status && <p className={styles.apoio}>{d.status}</p>}
      </div>

      {/* O veredito primeiro; o que a dimensão mede fica recolhido, para quem quer entender a régua. */}
      {d.leitura && <p className={styles.leitura}>{d.leitura}</p>}
      {avisos.length > 0 && (
        <InlineNotification kind={f?.avisoDoSaldo ? 'warning' : 'info'} lowContrast hideCloseButton
          title={avisos[0]} subtitle={avisos.slice(1).join(' ') || undefined}
          statusIconDescription={rotuloDoAviso(f?.avisoDoSaldo ? 'warning' : 'info')} aria-label={ROTULOS.fecharAviso} className={styles.aviso} />
      )}
      {d.dica && <DicaDaMaestra>{d.dica}</DicaDaMaestra>}
      {d.intro && (
        <Accordion className={styles.intro}>
          <AccordionItem title={d.intro.titulo}>
            <p className={styles.texto}>{d.intro.texto}</p>
          </AccordionItem>
        </Accordion>
      )}

      <div className={styles.colunas}>
        <div className={styles.coluna}>
          <H className={styles.subtitulo}>Os números</H>
          <Lista rotulo={`Os números de ${d.sub}`} linhas={numeros} />
          {d.linhas.some((l) => l.declarado) && (
            <p className={`${styles.apoio} ${styles.legendaDoDeclarado}`}>
              <UserSpeaker size={16} aria-hidden className={styles.iconeDoDeclarado} />
              Informado por quem preencheu o diagnóstico. A Maestra não verifica estes dados.
            </p>
          )}
          {d.convite && (
            <Tile className={styles.convite}>
              <Hsub className={styles.subtitulo}>De onde vem e pra onde vai</Hsub>
              <p className={styles.texto}>{d.convite}</p>
            </Tile>
          )}
        </div>

        {d.revela && (
          <div className={styles.coluna}>
            <H className={styles.subtitulo}>O que isso revela</H>
            {d.revela.titulo && <p className={styles.leitura}>{d.revela.titulo}</p>}
            {d.revela.paragrafos.map((p) => (
              <p key={p.lead} className={styles.texto}><strong>{p.lead}</strong>{p.corpo ? ` ${p.corpo}` : ''}</p>
            ))}
          </div>
        )}
      </div>

      {f && (
        <div className={styles.financas}>
          <H className={styles.subtitulo}>Saúde financeira · 12 meses</H>
          <div className={styles.numerosDaSaude}>
            {f.saude.map((s) => (
              <Tile key={s.rotulo} className={styles.numeroDaSaude}>
                <span className={styles.rotuloDoNumero}>{s.rotulo}</span>
                <span className={`${styles.valorDoNumero} ${s.sinal === 'negativo' ? styles.negativo : ''}`}>{s.valor}</span>
              </Tile>
            ))}
          </div>
          {f.notas.map((n) => <p key={n} className={styles.apoio}>{n}</p>)}
          <div className={styles.colunas}>
            {f.composicao.length > 0 && (
              <div className={styles.coluna}>
                <Hsub className={styles.subtitulo}>Composição da receita</Hsub>
                <Lista rotulo='Composição da receita' cabecas={['Fonte', 'Valor', 'Parte']}
                  linhas={composicao.map((c, i) => ({ chave: c.rotulo, celulas: [c.rotulo, c.texto, partes[i]] }))} />
                {f.naoInformado && <p className={styles.apoio}>{`Não informado: ${f.naoInformado.fontes}. ${f.naoInformado.aviso}`}</p>}
              </div>
            )}
            {f.cache.length > 0 && (
              <div className={styles.coluna}>
                <Hsub className={styles.subtitulo}>Cachê médio por tipo de contratante</Hsub>
                <Lista rotulo='Cachê médio por tipo de contratante' cabecas={['Contratante', 'Cachê médio']}
                  linhas={[...f.cache].sort((a, b) => b.valor - a.valor).map((c) => ({ chave: c.rotulo, celulas: [c.rotulo, c.texto] }))} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/** Os ouvintes por lugar: uma série só (uma cor), a maior em cima, os números por extenso curto. */
const GraficoDeOuvintes: FC<{ titulo: string; linhas: { nome: string; ouvintes: number }[] }> = ({ titulo, linhas }) => {
  const tema = useTemaDaPagina();
  // No celular, poucas marcas no eixo: com todas elas os números se atropelam.
  const doMd = useDoMd();
  // O Carbon desenha de baixo para cima: em ordem crescente, a maior fica em cima.
  const dados = [...linhas].sort((a, b) => a.ouvintes - b.ouvintes).map((l) => ({ group: 'Ouvintes', key: l.nome, value: l.ouvintes }));
  return (
    <Tile className={styles.grafico}>
      <h3 className={styles.subtitulo}>{titulo}</h3>
      <SimpleBarChart
        data={dados}
        options={{
          resizable: true,
          height: '240px',
          theme: tema,
          axes: {
            left: { mapsTo: 'key', scaleType: 'labels', truncation: { type: 'none' } },
            bottom: { mapsTo: 'value', ticks: { formatter: (v: unknown) => numeroCurto(Number(v)), rotation: 'never', ...(doMd ? {} : { number: 3 }) } },
          },
          tooltip: { valueFormatter: (v: unknown) => `${Number(v).toLocaleString('pt-BR')} ouvintes` },
          legend: { enabled: false },
          toolbar: { enabled: false },
          accessibility: { svgAriaLabel: titulo },
        } as never}
      />
    </Tile>
  );
};

export const RelatorioReal: FC<{
  r: DadosDoRelatorio;
  /** As ações de quem usa o relatório (baixar, refazer), ao lado do perfil. */
  acoes?: React.ReactNode;
  /** A ação do aviso de versão (refazer), quando há. */
  refazerDoAviso?: { rotulo: string; abrir: () => void };
  /**
   * As dimensões uma embaixo da outra, e não em abas. É o jeito da ENTREGA (criar e desbloquear o
   * perfil): quem acaba de receber o diagnóstico lê as quatro, sem precisar achar as abas. Na página
   * do Desenvolvimento, que se revisita, ficam as abas.
   */
  emSequencia?: boolean;
}> = ({ r, acoes, refazerDoAviso, emSequencia }) => {
  const [aba, setAba] = useState(0);
  const doMd = useDoMd();
  return (
    <div className={styles.relatorio}>
      <section className={styles.perfil} aria-labelledby='diagnostico-perfil'>
        <div className={styles.quem}>
          <span className={styles.rotulo}>Seu perfil de carreira</span>
          <h2 id='diagnostico-perfil' className={styles.nomeDoPerfil}>{r.perfil.nome}</h2>
          <LetrasReal grande dimensoes={r.perfil.dimensoes} />
          <p className={styles.retrato}>{r.perfil.retrato}</p>
        </div>
        {acoes && <div className={styles.acoes}>{acoes}</div>}
      </section>

      {r.avisoDeVersao && (refazerDoAviso ? (
        <AvisoComAcao kind='warning' lowContrast inline hideCloseButton
          title='Versão anterior do método' subtitle={r.avisoDeVersao}
          actionButtonLabel={refazerDoAviso.rotulo} onActionButtonClick={refazerDoAviso.abrir}
          statusIconDescription={rotuloDoAviso('warning')} aria-label={ROTULOS.fecharAviso} className={styles.aviso} />
      ) : (
        <InlineNotification kind='warning' lowContrast hideCloseButton title='Versão anterior do método'
          subtitle={r.avisoDeVersao} statusIconDescription={rotuloDoAviso('warning')} aria-label={ROTULOS.fecharAviso} className={styles.aviso} />
      ))}

      <Secao id='diagnostico-dimensoes' titulo='As quatro dimensões' texto='Onde a carreira está forte e onde ainda falta, dimensão por dimensão.'>
        {emSequencia ? (
          <div className={styles.emSequencia}>
            {/* O resumo das quatro no topo: a nota e o selo de cada uma, e o atalho até a seção dela. */}
            <nav aria-label='Ir para uma dimensão' className={styles.resumoDasDimensoes}>
              {r.dimensoes.map((d) => (
                <ClickableTile key={d.chave} href={`#dimensao-${d.chave}`} className={styles.itemDoResumo}
                  aria-label={`${d.letra} · ${d.sub}: ${d.nota != null ? `${d.nota} de 100, ` : ''}${d.selo}`}>
                  <span className={styles.letraDoResumo}>{`${d.letra} · ${d.sub}`}</span>
                  {d.nota != null && <span className={styles.notaDoResumo}>{d.nota}<span className={styles.notaMaxima}>/100</span></span>}
                  <Tag type={d.estado === 'acesa' || d.estado === 'topTier' ? 'green' : 'gray'} size='sm'>{d.selo}</Tag>
                </ClickableTile>
              ))}
            </nav>
            {r.dimensoes.map((d) => (
              <Tile key={d.chave} className={styles.painel}>
                <section id={`dimensao-${d.chave}`} aria-labelledby={`titulo-da-dimensao-${d.chave}`} className={styles.secaoDaDimensao}>
                  <div className={styles.tituloDaSequencia}>
                    <h3 id={`titulo-da-dimensao-${d.chave}`} className={styles.nomeDaDimensao}>{`${d.letra} · ${d.sub}`}</h3>
                    <Tag type={d.estado === 'acesa' || d.estado === 'topTier' ? 'green' : 'gray'} size='md'>{d.selo}</Tag>
                  </div>
                  <Layer><Dimensao d={d} nivel='h4' /></Layer>
                </section>
              </Tile>
            ))}
          </div>
        ) : (
          <div className={styles.dimensoes}>
            <Tabs selectedIndex={aba} onChange={({ selectedIndex }) => setAba(selectedIndex)}>
              {/* O selo mora na segunda linha da aba; no celular a aba leva só a letra (o nome inteiro vai no rótulo). */}
              <TabList aria-label='As quatro dimensões' contained fullWidth>
                {r.dimensoes.map((d) => (
                  <Tab key={d.chave} secondaryLabel={d.selo} aria-label={`${d.letra}, ${d.sub}: ${d.selo}`}>
                    {doMd ? `${d.letra} · ${d.sub}` : d.letra}
                  </Tab>
                ))}
              </TabList>
              <TabPanels>
                {/* O painel das abas contidas é uma camada acima da página: o que mora nele sobe mais uma. */}
                {r.dimensoes.map((d) => <TabPanel key={d.chave} className={styles.painel}><Layer><Dimensao d={d} /></Layer></TabPanel>)}
              </TabPanels>
            </Tabs>
          </div>
        )}
      </Secao>

      {r.insights.length > 0 && (
        <Secao id='diagnostico-revela' titulo='O que o seu diagnóstico revela'>
          <UnorderedList>{r.insights.map((i) => <ListItem key={i}>{i}</ListItem>)}</UnorderedList>
        </Secao>
      )}

      <Secao id='diagnostico-audiencia' titulo='Onde sua música encontra gente' texto='A distribuição dos ouvintes e a presença nas playlists.'>
        {!r.audiencia ? (
          <p className={styles.apoio}>Este diagnóstico não tem dados das plataformas de streaming.</p>
        ) : (
          <>
            {(r.audiencia.cidades.length > 0 || r.audiencia.paises.length > 0) && (
              <div className={styles.graficos}>
                {r.audiencia.cidades.length > 0 && <GraficoDeOuvintes titulo='Principais cidades' linhas={r.audiencia.cidades} />}
                {r.audiencia.paises.length > 0 && <GraficoDeOuvintes titulo='Principais países' linhas={r.audiencia.paises} />}
              </div>
            )}
            {r.audiencia.playlists.length > 0 && (
              <div className={styles.coluna}>
                <h3 className={styles.subtitulo}>Playlists onde sua música está</h3>
                {r.audiencia.totalDePlaylists && (
                  <p className={styles.apoio}>{`${r.audiencia.totalDePlaylists.toLocaleString('pt-BR')} no total. Estas são as maiores.`}</p>
                )}
                <TableContainer>
                  <Table size='md' aria-label='Playlists onde sua música está'>
                    <TableHead>
                      <TableRow>
                        <TableHeader>#</TableHeader>
                        <TableHeader>Playlist</TableHeader>
                        <TableHeader><span className={styles.numeroNaCelula}>Seguidores</span></TableHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {r.audiencia.playlists.map((p) => (
                        <TableRow key={`${p.posicao}-${p.nome}`}>
                          <TableCell>{p.posicao}</TableCell>
                          <TableCell>
                            <span className={styles.nomeNaTabela}>
                              {p.nome}
                              {p.editorial && <Tag type='blue' size='sm'>Editorial</Tag>}
                            </span>
                          </TableCell>
                          <TableCell><span className={styles.numeroNaCelula}>{p.seguidores ?? ''}</span></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            )}
          </>
        )}
      </Secao>

      <Secao id='diagnostico-perfis' titulo='Sua posição entre os 16 perfis' texto='Do Beginner, com nenhuma dimensão acesa, ao Icon, com as quatro.'>
        <TableContainer>
          <Table size='md' aria-label='Os 16 perfis'>
            <TableHead>
              <TableRow>
                <TableHeader>Acesas</TableHeader>
                <TableHeader>Perfil</TableHeader>
                <TableHeader>Dimensões</TableHeader>
                <TableHeader className={styles.soDoMd}>O que quer dizer</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {r.perfis.flatMap((andar) => andar.perfis.map((p, i) => (
                <TableRow key={p.nome} isSelected={p.atual}>
                  <TableCell>{i === 0 ? `${andar.altas} de 4` : ''}</TableCell>
                  <TableCell>
                    <span className={styles.nomeNaTabela}>
                      {p.nome}
                      {p.atual && <Tag type='high-contrast' size='sm'>Seu perfil</Tag>}
                    </span>
                  </TableCell>
                  <TableCell>{p.dimensoes && <LetrasReal dimensoes={p.dimensoes} rotulo={`${p.nome}: ${p.dimensoes.filter((x) => x.acesa).map((x) => x.letra).join(', ') || 'nenhuma dimensão acesa'}`} />}</TableCell>
                  <TableCell className={styles.soDoMd}>{p.leitura ?? ''}</TableCell>
                </TableRow>
              )))}
            </TableBody>
          </Table>
        </TableContainer>
      </Secao>
    </div>
  );
};
